'use client';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { BRAIN_COMPOSE_EVENT, BRAIN_OPEN_EVENT, advisorOpenTarget, openBrainComposer, type BrainComposeRequest, type BrainOpenRequest } from '../../lib/brainDock';
import { useMediaQuery, useMobileViewport } from '../../lib/useMobile';
import { Providers, type PluginUiSeed, type MeSeed } from '../../app/providers';
import { LanguageProvider, useTranslation, type Locale } from '../../lib/i18n';
import { SkinProvider } from '../../lib/skinContext';
import { type SkinCatalogue } from '../../lib/skins';
import { ToastProvider, resolveToastDuration } from '../ui/Toast';
import { useConfig, useMe, usePluginUi } from '../../lib/queries';
import type { PluginUiListing } from '../../lib/types';
import { LoginGate } from '../auth/LoginGate';
import { SidebarNav } from './SidebarNav';
import { TopBar } from './TopBar';
import { CommandPalette } from './CommandPalette';
import { AdvisorPanel } from '../../modules/advisor/AdvisorPanel';
import { BrainChat } from '../../modules/advisor/BrainChat';
import { Modal } from '../ui/Modal';
import { AdvisorLauncher } from '../../modules/advisor/AdvisorLauncher';
import { OperationDock } from '../ui/OperationDock';
import { MemoryMaintenanceProgressDialog } from '../../modules/memory/MemoryMaintenanceProgressDialog';
import { OperationDockProvider } from '../../lib/operationDock';
import { usePluginDockSources } from '../../lib/pluginOperationDockSources';
import { VoiceCallProvider } from '../../modules/voice/callController';
import { BrainChatProvider, useBrainChat } from '../../modules/advisor/BrainChatProvider';
import { ConversationSwitcherModal } from '../../modules/advisor/ConversationSwitcherModal';
import { ChatRailSplit } from '../../modules/advisor/ChatRailSplit';
import { TelemetryRailProvider } from '../../modules/advisor/telemetryRailState';
import { ImpersonationBanner } from './ImpersonationBanner';
import { StarterOfferDialog } from '../../modules/settings/StarterOfferDialog';
import { useDockState } from '../../lib/useDockState';
import { useElementWidth } from '../../lib/useElementWidth';
import { usePersistentState } from '../../lib/usePersistentState';
import { UiScaleProvider, useUiScale } from '../../lib/useUiScale';
import { PageHeaderProvider } from '../../lib/pageHeader';
import { usePagePath } from '../../lib/usePagePath';
import { hrefPathname, isPluginOverlayRoute, navigateWithinPlugin, openPluginOverlay, pluginListingForPath } from '../../lib/sameDocumentNavigation';
import { setPluginNavigate } from '../../lib/pluginUi';
import { RouteTransition } from './RouteTransition';
import { DocumentTitle } from './DocumentTitle';
import { EffectsProvider } from '../../lib/useEffects';
import { PageOverlayHost } from './PageOverlayHost';
import { syncPushSubscription } from '../../lib/pushClient';
import { authGeneration, subscribeAuthTransitions } from '../../lib/token';

/** How the rail presents itself, decided by the shell from the measured room it has. */
export type NavMode = 'full' | 'rail' | 'drawer';

/** The measure the interface is read at: `--content-max` (web/app/styles/tokens.css) is the single
 *  authority on how wide a document column may grow, so a skin can retune the reading measure without
 *  touching a component. */
const CONTENT_MAX = 'max-w-[var(--content-max)]';
/** The measure /chat is read at. It is a SECOND token rather than an exemption from the first: a
 *  conversation is an application layout — transcript, code, a telemetry column beside it — so it earns a
 *  wider frame than a page of records, but it is still a frame, centred on the same axis as every other
 *  route. Opting out entirely is what pinned the transcript to the left edge of a wide display. */
const CHAT_MAX = 'max-w-[var(--chat-max)]';
/** The measure a WORKBENCH page is read at — a plugin whose page is a tool with panes beside each other
 *  (the code editor's file tree, editor and diff) rather than a column of records. Third token, same
 *  reasoning as /chat's: an application layout earns more of the desk than a document does. A plugin
 *  cannot take it by writing a wider rule in its own stylesheet — this frame is above the plugin's markup
 *  in the DOM — so it DECLARES the measure in its manifest and the shell applies it here. */
const WORKBENCH_MAX = 'max-w-[var(--workbench-max)]';

/** Which of the three measures a route is read at. Named rather than boolean because the frame class and
 *  the `data-page-measure` attribute the inner page shell keys off must come from ONE decision. */
type PageMeasure = 'document' | 'chat' | 'workbench';
const MEASURE_CLASS: Record<PageMeasure, string> = { document: CONTENT_MAX, chat: CHAT_MAX, workbench: WORKBENCH_MAX };

/** The measure for a path, given the plugin UI listing the shell already holds for its navigation.
 *  Guides is the one core workbench (its pages column, article and outline sit side by side; the article
 *  text keeps its own reading measure). Otherwise only `/p/<plugin>` can be a workbench, and only if that
 *  plugin's manifest said so — an unknown plugin, an unloaded listing and an older daemon that sends no
 *  `layout` all read as the page measure. */
function pageMeasureFor(pathname: string | null, listing: PluginUiListing[] | undefined): PageMeasure {
  if (pathname === '/chat') return 'chat';
  if (pathname === '/guides' || pathname?.startsWith('/guides/')) return 'workbench';
  return pluginListingForPath(pathname, listing)?.layout === 'workbench' ? 'workbench' : 'document';
}


/** What the user pinned the navigation to, when the window is roomy enough to leave them the choice. */
type NavPin = 'full' | 'rail';
const NAV_PINS: readonly NavPin[] = ['full', 'rail'];

/** Studio follows the reference's tablet boundary: below 1024px its navigation is a full-width sheet. */
const COMMAND_NAV_COLUMN_MIN_WIDTH = 1024;
const STUDIO_DOCK_MIN_VIEWPORT = 1440;

/** The width sets a FLOOR on how compact the chrome is; the user's pin may only go compacter, never
 *  roomier. So the collapse handle is offered exactly when the pin is what decides — in a window already
 *  too narrow for the full rail, a toggle would be a dead control. Before the first measurement
 *  (`regionW === 0`) there is no reliable region decision; the Studio stylesheet owns the phone-width
 *  first-paint fail-safe so an SSR-stable unknown value cannot consume a 256px content column.
 *
 *  `regionW` is the MEASURED width of the nav+content region (window − advisor dock), in the same CSS
 *  pixels the stylesheet's media queries and `useMobileViewport()` read — see lib/breakpoints.ts. Using
 *  the region rather than the viewport is what lets dragging the dock re-chrome the app like a resize.
 */
/** @internal web/tests/app/shell.test.tsx characterizes the measured navigation boundary. */
export function resolveNav(regionW: number, pin: NavPin): { mode: NavMode; pinnable: boolean } {
  if (regionW === 0) return { mode: 'full', pinnable: false };
  if (regionW < COMMAND_NAV_COLUMN_MIN_WIDTH) return { mode: 'drawer', pinnable: false };
  return { mode: pin, pinnable: true };
}

/** Shared navigation inputs for the sidebar column and its drawer presentation. */
interface ShellNavProps {
  compact: boolean;
  measured: boolean;
  side: 'left' | 'right';
  drawer: boolean;
  drawerOpen: boolean;
  onDrawerClose: () => void;
  onToggleCollapse?: () => void;
}

function DockCloseBoundary({ children, close }: { children: (requestClose: () => void) => ReactNode; close: () => void }) {
  const { closeDock } = useBrainChat();
  return children(() => { void closeDock().then(allowed => { if (allowed) close(); }); });
}

function ShellOperationDockProvider({ children, chatVisible }: { children: ReactNode; chatVisible: boolean }) {
  const { activeSessionId } = useBrainChat();
  const pluginDock = usePluginDockSources();
  return <OperationDockProvider renderMemoryProgress={(operation, props) => <MemoryMaintenanceProgressDialog {...props} operation={operation} />} visibleSessionId={chatVisible ? activeSessionId : null}
    pluginSources={pluginDock.sources} resolvePluginSource={pluginDock.resolveSource}>{children}</OperationDockProvider>;
}

/** The chrome around one mounted page. `pathname` is the path of THAT page — see `usePagePath` —
 *  not necessarily the address in the bar: while an intercepted page overlay (`/settings`,
 *  `/account`) is open the address belongs to the overlay, and arranging the chrome around the
 *  overlay instead of the page would reflow the very page the overlay is laid over. */
function ShellLayout({ children, pathname, pageOverlay }: { children: ReactNode; pathname: string | null; pageOverlay: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const mobile = useMobileViewport();
  const { preference } = useUiScale();
  const studioDockViewport = useMediaQuery(`(min-width: ${STUDIO_DOCK_MIN_VIEWPORT}px)`);
  const onChat = pathname === '/chat';
  // The same listing the navigation is built from — one query, already cached by the time a plugin page
  // mounts, so asking it for the page measure costs no extra request.
  const { t, locale } = useTranslation();
  const listing = usePluginUi(locale).data;
  const measure = pageMeasureFor(pathname, listing);
  // A full-height application page — a workbench whose manifest also closes its own bottom edge
  // (`web.wordmark: false`) — owns the screen's edges on a phone: its frame fills the width and runs to
  // the bottom under the top bar. The attribute only names the decision; the geometry is CSS
  // (workspace-shell.css), so a wide window keeps the framed workbench exactly as before.
  const ownsEdges = measure === 'workbench' && pluginListingForPath(pathname, listing)?.wordmark === false;
  const dock = useDockState();
  const [explicitDock, setExplicitDock] = useState(false);
  const studioRightAvailable = studioDockViewport === true
    && !onChat
    && dock.state.side === 'right';
  const studioAskOpen = studioRightAvailable && dock.state.open;
  const [studioAdvisorMounted, setStudioAdvisorMounted] = useState(studioAskOpen);
  const [studioAdvisorVisible, setStudioAdvisorVisible] = useState(false);
  const [studioAskNavExpanded, setStudioAskNavExpanded] = useState(false);
  useEffect(() => {
    let frame: number | undefined;
    if (studioAskOpen) {
      setStudioAdvisorMounted(true);
      frame = requestAnimationFrame(() => setStudioAdvisorVisible(true));
    } else {
      setStudioAdvisorVisible(false);
    }
    return () => { if (frame !== undefined) cancelAnimationFrame(frame); };
  }, [studioAskOpen]);
  // Studio's reference chat rail is a desktop workspace column. Withhold it until the viewport is known,
  // below 1440px and on the full chat route, where it would duplicate the same controller beside itself.
  const docked = dock.state.open && !onChat && studioDockViewport === true;
  // On /chat the ChatView is the sole chat host, so the advisor panel is not docked beside it. This is a
  // UX guard only; the single controller in BrainChatProvider guarantees one SSE stream regardless of how
  // many surfaces mount elsewhere.
  const router = useRouter();
  const openAdvisor = useCallback(() => {
    if (studioDockViewport !== true) {
      if (!onChat) router.push('/chat');
      return;
    }
    const target = advisorOpenTarget({ onChat, mobile });
    if (target === 'none') return;
    if (target === 'chat-page') { router.push('/chat'); return; }
    dock.setOpen(true);
  }, [studioDockViewport, onChat, mobile, router, dock]);
  const openRequestedDock = useCallback(() => {
    if (onChat) return;
    setExplicitDock(true);
    dock.setOpen(true);
  }, [onChat, dock]);
  // Route a request to open or compose a conversation to its chat host. It lands on the full /chat page:
  // the switcher, a pinned conversation, the dashboard and the palette all open the real conversation
  // there. Only a request that asks for the dock (the advisor launcher, and the dock's own controls acting
  // on the conversation it shows) opens or keeps the side panel. On /chat the page IS the chat host — the
  // request loads there directly, so neither navigating nor popping the dock over it would add anything.
  // The single controller in BrainChatProvider loads the requested conversation wherever it lands.
  useEffect(() => {
    const route = (event: Event) => {
      if ((event as CustomEvent<BrainOpenRequest | BrainComposeRequest>).detail?.dock) { openRequestedDock(); return; }
      if (!onChat) router.push('/chat');
    };
    window.addEventListener(BRAIN_OPEN_EVENT, route);
    window.addEventListener(BRAIN_COMPOSE_EVENT, route);
    return () => {
      window.removeEventListener(BRAIN_OPEN_EVENT, route);
      window.removeEventListener(BRAIN_COMPOSE_EVENT, route);
    };
  }, [openRequestedDock, onChat, router]);
  // When the dock takes the left edge, the sidebar moves to the right edge (mirrored) so the two
  // never stack on the same side. Top/bottom docks span the full width above/below the row.
  const dockLeft = docked && dock.state.side === 'left';
  const dockTop = docked && dock.state.side === 'top';
  const dockBottom = docked && dock.state.side === 'bottom';
  // The launcher stays on every route; a phone does not show it (primitives.css). While a chat host is
  // already on screen, its "open" focuses that host's composer instead of mounting a second surface.
  const fullscreenDock = explicitDock && dock.state.open && !onChat && studioDockViewport === false;
  const chatVisible = onChat || docked || fullscreenDock;
  const openLauncher = useCallback(() => {
    if (chatVisible) openBrainComposer('', { dock: true });
    else openAdvisor();
  }, [chatVisible, openAdvisor]);
  const viewportRef = useRef<HTMLDivElement>(null);
  const pageColumnRef = useRef<HTMLDivElement>(null);

  // This column already excludes navigation, telemetry and advisor panels on every side, including a
  // panel docked below it. Publish its real edges instead of keeping another model of the panels' sizes.
  useLayoutEffect(() => {
    const viewport = viewportRef.current!;
    const column = pageColumnRef.current!;
    const geometry = document.documentElement.style;
    const update = () => {
      const frame = viewport.getBoundingClientRect();
      const page = column.getBoundingClientRect();
      geometry.setProperty('--shell-operation-right', `${(frame.right - page.right) / preference}px`);
      geometry.setProperty('--shell-operation-bottom', `${(frame.bottom - page.bottom) / preference}px`);
      geometry.setProperty('--shell-operation-available-width', `${page.width / preference}px`);
    };
    const observer = new ResizeObserver(update);
    for (const node of [viewport, column]) observer.observe(node);
    update();
    return () => {
      observer.disconnect();
      for (const key of ['--shell-operation-right', '--shell-operation-bottom', '--shell-operation-available-width']) geometry.removeProperty(key);
    };
  }, [pathname, docked, dock.state.side, mobile, preference]);
  const studioAskPanelMounted = studioRightAvailable && studioAdvisorMounted;

  // Measure the region the sidebar + content actually share (everything but a left/right dock). The
  // sidebar's mode (full / rail / drawer) and the mobile top bar key off THIS, so the chrome reacts to
  // real available space. Content inside <main> reacts to its own width via CSS container queries.
  const [regionRef, regionW, regionMeasured] = useElementWidth<HTMLDivElement>();
  const [pin, setPin] = usePersistentState<NavPin>('elowen.nav.command.pin', 'full', NAV_PINS);
  const navResolution = resolveNav(regionW, pin);
  const studioAskControlsNav = studioAskOpen && navResolution.mode !== 'drawer';
  const mode = studioAskControlsNav ? (studioAskNavExpanded ? 'full' : 'rail') : navResolution.mode;
  const pinnable = navResolution.pinnable;
  const toggleNav = studioAskControlsNav
    ? () => setStudioAskNavExpanded((expanded) => !expanded)
    : pinnable
      ? () => setPin(pin === 'rail' ? 'full' : 'rail')
      : undefined;
  // Widening past the drawer breakpoint replaces the drawer with a column, but the open flag would
  // survive — so narrowing again, without ever touching the menu, would slide it back out on its own.
  useEffect(() => { if (mode !== 'drawer') setDrawerOpen(false); }, [mode]);

  // One menu at every width. A phone gets the same menu, in its full labelled form, slid in over the
  // content; wider windows give it a column of its own. Collapsing is offered only where the pin is
  // what decides, and never in the drawer, where there is no column to narrow.
  const navProps: ShellNavProps = {
    compact: mode === 'rail',
    measured: regionMeasured,
    side: dockLeft ? 'right' : 'left',
    drawer: mode === 'drawer',
    drawerOpen,
    onDrawerClose: () => setDrawerOpen(false),
    onToggleCollapse: toggleNav,
  };
  // ONE navigation at every width and under every design. It used to be swapped on the shell profile —
  // a spatial rail beside a flat column — and both are gone: the app has a single sidebar now, and a
  // skin restyles it by overriding the `--sidebar-*` tokens rather than by mounting a different tree.
  //
  // Everything below — the brain-chat provider, the viewport box, <main> and its scroll position, the
  // route content, the command palette, the advisor launcher — is owned by this one component and mounts
  // exactly once. Branching any higher up (`{studio ? <StudioLayout/> : <ShellLayout/>}`) would make
  // React see two different component types and unmount that whole subtree on a skin switch, which drops
  // the live conversation BrainChatProvider exists to keep across dock toggles and route changes.
  const navigation = <SidebarNav {...navProps} regionWidth={regionW} scale={preference} />;
  // The ruled bar stays mounted on /chat at every width. The conversation toolbar portals into its
  // page slot, carrying the phone controls beside the navigation button.
  const topBar = (
    <TopBar
      onMenuClick={mode === 'drawer' ? () => setDrawerOpen(true) : undefined}
      onNavToggle={mode !== 'drawer' ? toggleNav : undefined}
      navCollapsed={mode === 'rail'}
      navSide={dockLeft ? 'right' : 'left'}
    />
  );
  const content = (
    <div ref={pageColumnRef} className="shell-page-column flex min-h-0 min-w-0 flex-1 flex-col">
      {topBar}
      {/* The operation column reserves an inner right inset; the scroller keeps its full height. */}
      {/* NOTE: no `container-type` here on purpose — it would make <main> a containing block for
          `position: fixed` descendants and re-anchor any non-portaled overlay (full-screen modals,
          context menus) to it. Content views scope their own `@container` around just the grid/list
          instead, keeping overlays outside it. */}
      <main
        data-scroll-owner={onChat ? 'chat-shell' : 'page'}
        className={`flex min-h-0 flex-1 flex-col ${onChat
          ? 'overflow-clip'
          : 'overflow-x-hidden overflow-y-auto overscroll-contain [scrollbar-gutter:stable]'}`}
      >
        {/* The measure the interface is read at. Without it the content is purely fluid and every extra
            pixel of a wide monitor goes into stretching the same table across a wider, emptier row.
            Capping it keeps a table's density tied to its type size instead of to the window. The
            heading rides inside the cap so it stays aligned with the content beneath it. */}
        {/* /chat reads its own measure. It is not a document but an application layout — a conversation
            column with a telemetry rail docked beside it — so it is capped wider than a page of records,
            and centred on the same axis so the rail sits inside the frame rather than out at the window
            edge with the transcript stranded opposite it. */}
        {/* A plugin page that DECLARED itself a workbench reads at the third measure, wider still. The
            declaration lives in its manifest and arrives on the nav listing, so the frame is right on the
            first paint; the attribute carries the same one decision to CSS, where the page surfaces inside
            this frame stand down rather than capping the workbench a second time. */}
        <div
          data-page-measure={measure}
          data-page-edges={ownsEdges ? 'own' : undefined}
          className={`mx-auto flex w-full flex-col ${MEASURE_CLASS[measure]} ${onChat ? 'min-h-0 flex-1' : ''}`}
        >
          {/* Named so a design can state its own gutter here. The 8px inset is the built-in design's:
              it sits OUTSIDE `.workspace-shell`, which carries `--shell-gutter` of its own, so the two
              add up and a design wanting one exact gutter has to be able to reach this one. */}
          {/* The page ending lives in primitives.css beside the launcher it clears. /chat has none: it owns
              its bottom edge through the fixed surface, visual viewport and safe-area policy, and a page
              ending there floated the focused composer a full band above the keyboard. */}
          <div
            className={`shell-content px-2 ${onChat ? 'flex min-h-0 flex-1 flex-col' : ''}`}
          >
            <RouteTransition pathname={pathname}>{children}</RouteTransition>
          </div>
        </div>
      </main>
    </div>
  );

  // The single brain-chat controller lives here — ONE mount above both the route content and every
  // dock-side AdvisorPanel, so its SSE stream / transcript / draft survive dock open-close, the
  // Chat↔Terminál toggle and route changes. It is inert until the first chat open (lazy ensureAttached).
  // The sidebar + content region — the dock sits OUTSIDE it, so this width = window − dock.
  const workspaceRegion = (
    <div ref={regionRef} className="shell-workspace flex min-w-0 flex-1 overflow-hidden" data-studio-ask-open={studioAskOpen || undefined}>
      {dockLeft ? <>{content}{navigation}</> : <>{navigation}{content}</>}
    </div>
  );
  // Every route keeps the workspace in one stable resizable group HERE, a direct child of the
  // full-height workspace row. On a measured desktop the group adds the telemetry panel; on a phone it
  // contains only the conversation and ChatView raises the same rail as a full-screen overlay. Keeping the
  // group and content panel mounted across that breakpoint preserves the chat's focus, draft and scroll.
  // Shell-level ownership is what lets the desktop rail sit flush on the viewport's top/right/bottom edges;
  // inside ChatView it was trapped under the top bar and inside the centred `--chat-max` frame.
  const railDocked = mobile === false;

  return (
    <BrainChatProvider>
      <ShellOperationDockProvider chatVisible={chatVisible}>
      <VoiceCallProvider>
      <TelemetryRailProvider>
      {/* The app shell is exactly one viewport tall, and `dvh` so a phone's collapsing URL bar does not
          leave a dead strip. The division survives because the Account UI-scale preference still puts a
          `zoom` on <html>: a `100dvh` box under `zoom: z` renders at z×viewport, so filling the screen
          means asking for `100dvh / z`. At the default scale of 1 it is plain `100dvh`. */}
      <div ref={viewportRef} className="shell-viewport flex flex-col overflow-hidden" style={{ height: 'calc(100dvh / var(--ui-scale, 1))' }}>
        <ImpersonationBanner />
        {dockTop ? <DockCloseBoundary close={() => dock.setOpen(false)}>{requestClose => <AdvisorPanel dock={dock} onClose={requestClose} />}</DockCloseBoundary> : null}
        <div className="shell-workspace-row flex min-h-0 flex-1 overflow-hidden" data-studio-ask-open={studioAskOpen || undefined}>
          {dockLeft ? <DockCloseBoundary close={() => dock.setOpen(false)}>{requestClose => <AdvisorPanel dock={dock} onClose={requestClose} />}</DockCloseBoundary> : null}
          <ChatRailSplit workspace={workspaceRegion} docked={onChat && railDocked} />
          {studioAskPanelMounted ? (
            <div
              className="studio-advisor-slot flex h-full shrink-0"
              data-open={studioAskOpen || undefined}
              style={{ width: studioAskOpen ? `calc(${dock.state.width}px + var(--spacing) * 2)` : 0 }}
            >
              <DockCloseBoundary close={() => dock.setOpen(false)}>{requestClose => <AdvisorPanel dock={dock} onClose={requestClose} visible={studioAdvisorVisible} presentation="studio-ask" />}</DockCloseBoundary>
            </div>
          ) : docked && dock.state.side === 'right' ? <DockCloseBoundary close={() => dock.setOpen(false)}>{requestClose => <AdvisorPanel dock={dock} onClose={requestClose} />}</DockCloseBoundary> : null}
        </div>
        {dockBottom ? <DockCloseBoundary close={() => dock.setOpen(false)}>{requestClose => <AdvisorPanel dock={dock} onClose={requestClose} />}</DockCloseBoundary> : null}
        <OperationDock />
        <AdvisorLauncher onOpen={openLauncher} />
      </div>
      {fullscreenDock ? <DockCloseBoundary close={() => dock.setOpen(false)}>{requestClose => <Modal title={t.advisor.title} presentation="fullscreen" onClose={requestClose}><div className="flex h-full min-h-0 flex-col"><BrainChat /></div></Modal>}</DockCloseBoundary> : null}
      <CommandPalette />
      {/* THE conversation switcher, mounted once beside the controller that holds its open state: every
          entry point — /chat's header, the dock's conversation name — opens this one, so the app has a
          single conversation list rather than a rail, a drawer, a popover and a register modal. */}
      <ConversationSwitcherModal />
      {/* The persistent frame portals beside the backing page and shares its providers.
          Route slots replace only the content inside it, never the frame itself. */}
      <PageOverlayHost>{pageOverlay}</PageOverlayHost>
      </TelemetryRailProvider>
      </VoiceCallProvider>
      </ShellOperationDockProvider>
    </BrainChatProvider>
  );
}


/** Renders the full app chrome inside the providers and auth gate. */
function ShellBody({ children, pageOverlay }: { children: ReactNode; pageOverlay: ReactNode }) {
  const { locale } = useTranslation();
  const pluginUi = usePluginUi(locale);
  const router = useRouter();
  useEffect(() => {
    // Dock and card callers can navigate before any plugin page has mounted.
    setPluginNavigate((href, options) => {
      if (isPluginOverlayRoute(hrefPathname(href), pluginUi.data)) openPluginOverlay(href, options);
      else if (navigateWithinPlugin(href, options)) return;
      else if (options?.replace) router.replace(href);
      else router.push(href);
    });
  }, [pluginUi.data, router]);
  const pagePath = usePagePath(pluginUi.data);
  const me = useMe();
  const generation = authGeneration();
  useEffect(() => {
    if (!me.data) return;
    const sync = () => { void syncPushSubscription().catch(error => console.error('Push subscription sync failed', error)); };
    sync();
    return subscribeAuthTransitions(transition => { if (transition.phase === 'ready') sync(); });
  }, [generation, me.data]);
  return <><ShellLayout pathname={pagePath} pageOverlay={pageOverlay}>{children}</ShellLayout><StarterOfferDialog automatic /></>;
}

/** The toast provider bound to the operator's configured duration (Settings → Elowen AI → Runtime). It
 *  sits above the auth gate, where the config query 401s for a visitor who has not logged in yet — the
 *  same race every other child query runs — so an unresolved config simply leaves the default in force. */
function ConfiguredToastProvider({ children }: { children: ReactNode }) {
  const { data: config } = useConfig();
  return <ToastProvider durationMs={resolveToastDuration(config?.runtime?.limits)}>{children}</ToastProvider>;
}

/** Enable daemon persistence only after the shared identity query proves the browser is signed in. */
function SyncedLanguageProvider({ children, initialLocale }: { children: ReactNode; initialLocale?: Locale }) {
  const me = useMe();
  const qc = useQueryClient();
  const localeChanged = useCallback(() => {
    void qc.cancelQueries({ queryKey: ['my-alert-kinds'] }).then(() => qc.invalidateQueries({ queryKey: ['my-alert-kinds'] }));
  }, [qc]);
  return <LanguageProvider initialLocale={initialLocale} syncServer={!!me.data} onServerLocaleChanged={localeChanged}>{children}</LanguageProvider>;
}

export function Shell({ children, pageOverlay = null, pluginUiSeed, meSeed, sessionPresent = true, initialLocale, skinSeed }: { children: ReactNode; pageOverlay?: ReactNode; pluginUiSeed?: PluginUiSeed | null; meSeed?: MeSeed | null; sessionPresent?: boolean; initialLocale?: Locale; skinSeed?: { catalogue: SkinCatalogue; choice: string | null; allowed: string[]; fallback: string | null } }) {
  return (
    <EffectsProvider>
      <Providers pluginUiSeed={pluginUiSeed} meSeed={meSeed}>
        <UiScaleProvider>
        <SyncedLanguageProvider initialLocale={initialLocale}>
          <SkinProvider catalogue={skinSeed?.catalogue} initialChoice={skinSeed?.choice ?? null} allowedSkins={skinSeed?.allowed} fallback={skinSeed?.fallback ?? null}>
        <ConfiguredToastProvider>
          <PageHeaderProvider>
            {/* One title owner for login and every authenticated route; terminal names itself. */}
            <DocumentTitle />
            <LoginGate initiallyAuthenticated={meSeed != null} sessionPresent={sessionPresent}>
              <ShellBody pageOverlay={pageOverlay}>{children}</ShellBody>
            </LoginGate>
          </PageHeaderProvider>
        </ConfiguredToastProvider>
          </SkinProvider>
        </SyncedLanguageProvider>
        </UiScaleProvider>
      </Providers>
    </EffectsProvider>
  );
}
